import { spawnSync } from "node:child_process";

import { readTextIfExists } from "./fs.js";
import { readOpenClawGatewayPort } from "../surfaces/openclaw-config.js";

export interface OpenClawHealthCheck {
  ok: boolean;
  skipped: boolean;
  method: "cli" | "http" | "none";
  url?: string;
  details: string[];
  error?: string;
}

function trimOutput(value: string | null | undefined): string | undefined {
  const next = value?.trim();
  return next ? next : undefined;
}

export async function checkOpenClawHealth(
  configPath?: string,
): Promise<OpenClawHealthCheck> {
  const rawConfig = configPath ? readTextIfExists(configPath) : null;
  const port = readOpenClawGatewayPort(rawConfig);
  const url = `http://127.0.0.1:${port}/orgx/live`;

  const cli = spawnSync("openclaw", ["gateway", "status"], {
    encoding: "utf8",
    timeout: 5_000,
  });

  if (!cli.error) {
    const stdout = trimOutput(cli.stdout);
    const stderr = trimOutput(cli.stderr);
    const details = [`gateway port: ${port}`];
    if (stdout) details.push(`cli: ${stdout}`);
    if (stderr) details.push(`cli stderr: ${stderr}`);

    if (cli.status === 0) {
      return {
        ok: true,
        skipped: false,
        method: "cli",
        url,
        details,
      };
    }
  }

  try {
    const response = await fetch(url, {
      redirect: "manual",
      signal: AbortSignal.timeout(5_000),
    });

    if (response.ok || (response.status >= 300 && response.status < 400)) {
      return {
        ok: true,
        skipped: false,
        method: "http",
        url,
        details: [`gateway port: ${port}`, `http status: ${response.status}`],
      };
    }

    return {
      ok: false,
      skipped: false,
      method: "http",
      url,
      error: `HTTP ${response.status}`,
      details: [`gateway port: ${port}`, `http status: ${response.status}`],
    };
  } catch (error) {
    const cliError = cli.error instanceof Error ? cli.error.message : undefined;
    return {
      ok: false,
      skipped: false,
      method: cliError ? "none" : "http",
      url,
      error: error instanceof Error ? error.message : String(error),
      details: [
        `gateway port: ${port}`,
        ...(cliError ? [`openclaw cli unavailable: ${cliError}`] : []),
      ],
    };
  }
}
